/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Loader from '../../../../Components/Loader';
import { useAuth } from '../../../../Hooks/useAuth';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../../Api/resources/serviceURLs';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import UseAxios from '../../../../Api/http/http';
import { getProjectDetailsByProjectId } from '../../../../Api/request/projectDetails';
import {
  createTimelineRequest,
  updateTimelineRequest,
  updateTimelineRequestStatus,
} from '../../../../Api/request/timelineChangeRequest';
import { masterProjectNameData } from '../../../../Api/request/projectOverviewMasterData';
import { MODULE_NAMES } from '../../../../Constants/modules';
import './Timeline.scss';
import { buildTimelineTableColumns } from './timelineConfig';

import { TIMELINE_DATE_TYPE_OPTIONS } from './constants';
import {
  TimelineRequestDialog,
  TimelineStatusDialog,
} from './components/TimelineDialogs';
import EditIcon from '../../../../Assets/images/svg/dar_pencil.svg';
import normalizeTableUtilityResponse from '../../../../Utils/tableUtilityResponse';

const __STATUS_CLASS_MAP = {
  Approved: 'status-approved',
  Rejected: 'status-rejected',
  Draft: 'status-draft',
  Open: 'status-open',
};
const STATUS_UPDATE_OPTIONS = ['Approved', 'Rejected'];
const buildServiceURL = (service, path) => {
  const serviceURL = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${serviceURL}${path}`;
  }

  const servicePort = appendServicePortToURL(service);
  const apiBase = String(process.env.REACT_APP_API_URL || '').replace(
    /\/$/,
    '',
  );
  return `${apiBase}:${servicePort}${serviceURL}${path}`;
};

const TIMELINE_TABLE_BASE_URL = buildServiceURL(
  serviceName.PROJECT_MS,
  '/api/v1/timeline-change-request',
);

const isAxiosFailurePayload = (payload) =>
  Boolean(payload) && (payload?.name === 'AxiosError' || payload?.isAxiosError);

const getApiErrorMessage = (errorOrPayload, fallbackMessage) => {
  const responseData
    = errorOrPayload?.response?.data || errorOrPayload?.data || {};
  const axiosDataMessage
    = responseData?.message
    || responseData?.error?.message
    || responseData?.error
    || responseData?.detail;
  const axiosMessage = errorOrPayload?.message;
  const directMessage
    = errorOrPayload?.data?.message
    || errorOrPayload?.data?.error?.message
    || errorOrPayload?.data?.error
    || errorOrPayload?.data?.detail;

  if (
    responseData?.code === 'OPEN_TIMELINE_REQUEST_EXISTS'
    || directMessage
    === 'An open timeline change request already exists for this project'
    || axiosDataMessage
    === 'An open timeline change request already exists for this project'
  ) {
    return 'An open timeline change request already exists for this project';
  }

  return axiosDataMessage || directMessage || axiosMessage || fallbackMessage;
};

const MONTH_MAP = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

export const parseDisplayDateToInput = (value) => {
  if (!value || value === '-') return '';
  const parts = String(value).split('-');
  if (parts.length !== 3) return '';
  const [day, monthAbbr, year] = parts;
  const month = MONTH_MAP[monthAbbr];
  if (!month) return '';
  return `${year}-${month}-${day.padStart(2, '0')}`;
};

const MONTH_ABBRS = Object.keys(MONTH_MAP);
export const formatTableDate = (value) => {
  if (!value || value === '-') return value || '-';
  const raw = String(value).trim();
  if (/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(raw)) return raw;
  const datePart = raw.split('T')[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) return raw;
  const [, year, monthNum, day] = match;
  const monthIndex = Number(monthNum) - 1;
  return `${day}-${MONTH_ABBRS[monthIndex] || monthNum}-${year}`;
};

const toNumericProjectId = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const getProjectNameMasterRows = (response = {}) =>
  response?.data?.data?.rows
  || response?.data?.rows
  || response?.data?.data?.masterProjectNameDetails
  || response?.data?.masterProjectNameDetails
  || response?.data?.data?.masterProjectNames
  || response?.data?.masterProjectNames
  || response?.data?.data?.projectNames
  || response?.data?.projectNames
  || response?.data?.data?.masterProjectList
  || response?.data?.masterProjectList
  || [];

const normalizeProjectMasterOptions = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((item) => {
      const name = String(
        item?.masterProjectName
        ?? item?.master_project_name
        ?? item?.projectName
        ?? item?.project_name
        ?? item?.name
        ?? item?.label
        ?? '',
      ).trim();
      const projectId = toNumericProjectId(
        item?.masterProjectId
        ?? item?.master_project_id
        ?? item?.projectId
        ?? item?.project_id
        ?? item?.id
        ?? item?.value,
      );
      if (!name || !projectId) return null;
      return { projectId, name };
    })
    .filter(Boolean);

const normalizeTimelineFilterOptions = (filterOptions) => {
  if (Array.isArray(filterOptions)) {
    return filterOptions.reduce((optionsByKey, filterGroup) => {
      if (filterGroup?.key) {
        optionsByKey[filterGroup.key] = Array.isArray(filterGroup.options)
          ? filterGroup.options
          : [];
      }
      return optionsByKey;
    }, {});
  }

  return filterOptions && typeof filterOptions === 'object'
    ? filterOptions
    : {};
};

export const resolveProjectInfo = (raw, masterOptions = [], rows = []) => {
  if (raw === null || raw === undefined || raw === '') {
    return { projectId: '', projectName: '', projectVertical: '' };
  }

  let rawId = '';
  let rawName = '';

  if (typeof raw === 'object' && raw !== null) {
    const candidateId = raw.projectId ?? raw.project_id ?? raw.id ?? raw.value;
    const candidateName = raw.projectName ?? raw.project_name ?? raw.name ?? raw.label;
    if (candidateId !== undefined && candidateId !== null) rawId = String(candidateId).trim();
    if (candidateName !== undefined && candidateName !== null) rawName = String(candidateName).trim();
  } else {
    const str = String(raw).trim();
    if (/^\d+$/.test(str)) {
      rawId = str;
    } else {
      rawName = str;
    }
  }

  // 1. Try finding in master options
  const optionsList = Array.isArray(masterOptions) ? masterOptions : [];
  if (optionsList.length > 0) {
    const matched = optionsList.find((opt) => {
      const optId = String(opt?.projectId || opt?.id || opt?.value || '').trim();
      const optName = String(opt?.name || opt?.label || '')
        .trim()
        .toLowerCase();
      if (rawId && optId === rawId) return true;
      if (rawName && optName === rawName.toLowerCase()) return true;
      if (typeof raw === 'string') {
        const strLower = raw.trim().toLowerCase();
        if (optId === raw.trim() || optName === strLower) return true;
      }
      return false;
    });

    if (matched) {
      return {
        projectId: String(matched.projectId || matched.id || matched.value || ''),
        projectName: String(matched.name || matched.label || ''),
        projectVertical: matched.projectVertical || matched.project_vertical || '',
      };
    }
  }

  // 2. Try finding in rows
  const rowsList = Array.isArray(rows) ? rows : [];
  if (rowsList.length > 0) {
    const matched = rowsList.find((row) => {
      const rowId = String(row?.projectId || row?.project_id || '').trim();
      const rowName = String(row?.projectName || row?.project_name || '')
        .trim()
        .toLowerCase();
      if (rawId && rowId === rawId) return true;
      if (rawName && rowName === rawName.toLowerCase()) return true;
      if (typeof raw === 'string') {
        const strLower = raw.trim().toLowerCase();
        if (rowId === raw.trim() || rowName === strLower) return true;
      }
      return false;
    });

    if (matched) {
      return {
        projectId: String(matched.projectId || matched.project_id || ''),
        projectName: String(matched.projectName || matched.project_name || ''),
        projectVertical: matched.projectVertical || matched.project_vertical || '',
      };
    }
  }

  return {
    projectId: rawId,
    projectName: rawName || (rawId ? '' : String(raw || '').trim()),
    projectVertical: '',
  };
};

const createEmptyRequest = (projectName = '') => ({
  projectName,
  requestedFieldValue: TIMELINE_DATE_TYPE_OPTIONS[0],
  approvalLink: '',
  newDateValue: '',
  reasonForChange: '',
});

const matchesTimelineScope = (
  row = {},
  scopedProjectId = '',
  scopedProjectName = '',
) => {
  const normalizedScopedProjectName = String(scopedProjectName || '')
    .trim()
    .toLowerCase();
  const normalizedRowProjectName = String(row?.projectName || '')
    .trim()
    .toLowerCase();

  if (normalizedScopedProjectName) {
    return normalizedRowProjectName === normalizedScopedProjectName;
  }

  if (scopedProjectId) {
    return String(row?.projectId || '') === String(scopedProjectId);
  }

  return true;
};

const isOpenTimelineRequest = (row = {}) =>
  String(row?.requestStatus || '')
    .trim()
    .toLowerCase() === 'open';

const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  limit: 10,
  total: 0,
});

const syncPaginationFromResponse = (setPagination, response) => {
  const responseData
    = response?.data && typeof response.data === 'object' ? response.data : {};
  const payload
    = responseData?.data && typeof responseData.data === 'object'
      ? responseData.data
      : responseData;
  const meta
    = payload?.meta && typeof payload.meta === 'object'
      ? payload.meta
      : responseData?.meta && typeof responseData.meta === 'object'
        ? responseData.meta
        : {};

  setPagination((previousPagination) => {
    const nextPage
      = Number(meta.page) > 0 ? Number(meta.page) : previousPagination.page;
    const nextLimit
      = Number(meta.limit) > 0 || Number(meta.pageSize) > 0
        ? Number(meta.limit) || Number(meta.pageSize)
        : previousPagination.limit;
    const nextTotal
      = Number(meta.total) >= 0 ? Number(meta.total) : previousPagination.total;

    if (
      previousPagination.page === nextPage
      && previousPagination.limit === nextLimit
      && previousPagination.total === nextTotal
    ) {
      return previousPagination;
    }

    return {
      ...previousPagination,
      page: nextPage,
      limit: nextLimit,
      total: nextTotal,
    };
  });
};

const normalizeFilterSelectionValue = (selectedValue) => {
  if (
    selectedValue === null
    || selectedValue === undefined
    || selectedValue === ''
  ) {
    return null;
  }
  if (typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return (
      selectedValue.value
      ?? selectedValue.id
      ?? selectedValue.name
      ?? selectedValue.label
      ?? null
    );
  }
  return selectedValue;
};

const cloneSelectedValues = (selectedValues = []) =>
  Array.isArray(selectedValues)
    ? selectedValues
      .filter(Boolean)
      .map(normalizeFilterSelectionValue)
      .filter((val) => val !== null)
    : [];

const TimelineSelectionContext = React.createContext(null);

const SrnHeaderCell = () => {
  const context = React.useContext(TimelineSelectionContext);
  if (!context) return null;
  const {
    isSomeSelected,
    isAllVisibleRowsSelected,
    handleToggleAllRows,
    canApproveTimelineRequest,
  } = context;
  return (
    <div className="timeline-srn-header-cell">
      {canApproveTimelineRequest && (
        <input
          type="checkbox"
          className={`timeline-checkbox ${isSomeSelected ? 'is-indeterminate' : ''}`}
          checked={isAllVisibleRowsSelected}
          onChange={(event) => handleToggleAllRows(event.target.checked)}
        />
      )}
      <span className="timeline-srn-header-label">SRN #</span>
    </div>
  );
};

const SrnRowCell = ({ rowId, srnNo, requestStatus }) => {
  const context = React.useContext(TimelineSelectionContext);
  if (!context) return null;
  const { selectedRowIds, setSelectedRowIds, canApproveTimelineRequest }
    = context;
  const checked = selectedRowIds.includes(rowId);
  const status = String(requestStatus || '')
    .trim()
    .toLowerCase();
  const isOpen = status === 'open' || status === '';
  const disabledTitle = isOpen
    ? ''
    : 'Only open requests can be selected for bulk update';

  return (
    <div className="timeline-srn-cell">
      {canApproveTimelineRequest && (
        <input
          type="checkbox"
          className="timeline-checkbox"
          checked={checked}
          disabled={!isOpen}
          title={disabledTitle}
          onChange={(event) => {
            setSelectedRowIds((current) =>
              event.target.checked
                ? current.includes(rowId)
                  ? current
                  : [...current, rowId]
                : current.filter((item) => item !== rowId),
            );
          }}
        />
      )}
      <span className="timeline-srn-text">{srnNo || '-'}</span>
    </div>
  );
};

SrnRowCell.propTypes = {
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  srnNo: PropTypes.string,
  requestStatus: PropTypes.string,
};

SrnRowCell.defaultProps = {
  srnNo: '',
  requestStatus: '',
};

const TimelineToolbarActions = () => {
  const context = React.useContext(TimelineSelectionContext);
  if (!context) return null;
  const {
    selectedRowIds,
    handleOpenBulkStatusDialog,
    canApproveTimelineRequest,
  } = context;

  if (!canApproveTimelineRequest) return null;

  return (
    <>
      <span className="timeline-toolbar-selection">
        Timeline requests selected : {selectedRowIds.length}
      </span>
      <button
        type="button"
        className="timeline-bulk-btn timeline-bulk-btn--toolbar"
        onClick={handleOpenBulkStatusDialog}
        disabled={!selectedRowIds.length}
        title={
          !selectedRowIds.length
            ? 'Select timeline requests to update status'
            : 'Update status of selected requests'
        }
      >
        Update Status
      </button>
    </>
  );
};
const Timeline = () => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const stableUserProfile = useMemo(
    () => userProfile,
    [userProfile?.access_token, userProfile?.email, userProfile?.employeeId],
  );

  const isSuperAdmin = useMemo(() => {
    if (!userProfile || Array.isArray(userProfile)) return false;
    const superAdminFlag = userProfile?.superAdmin ?? userProfile?.super_admin ?? userProfile?.isSuperAdmin;
    if (Number(superAdminFlag) === 1 || superAdminFlag === true) return true;
    const roleDetails = Array.isArray(userProfile?.roleDetails)
      ? userProfile.roleDetails
      : userProfile?.roleDetails
        ? [userProfile.roleDetails]
        : [];
    return roleDetails.some((role) => {
      const roleName = String(role?.roleName || role?.role_name || role?.name || '')
        .trim()
        .toLowerCase();
      const roleId = Number(role?.roleId || role?.role_id || role?.id);
      return roleName === 'super admin' || roleId === 1;
    });
  }, [userProfile]);

  const timelinePermissions = useMemo(() => {
    if (!userProfile) return [];
    const rawPerms = userProfile.permissions;
    if (!rawPerms) return [];

    const targetModuleKeys = [
      MODULE_NAMES.PROJECT_TIMELINE_CHANGE_REQUEST,
      'delivery:project-overview:timeline-change-request',
      'delivery:project_overview:timeline_change_request',
      'project:timeline-change-request',
      'project:timeline_change_request',
      'project:change-request:timeline',
      'project:timeline',
    ];

    const actions = [];
    if (Array.isArray(rawPerms)) {
      rawPerms.forEach((item) => {
        if (typeof item === 'string') {
          const lower = item.toLowerCase();
          if (targetModuleKeys.some((targetKey) => lower === targetKey.toLowerCase() || lower.includes('timeline'))) {
            actions.push(lower);
          }
        } else if (item && typeof item === 'object') {
          const mod = String(item.module || item.name || '').toLowerCase();
          if (
            targetModuleKeys.some(
              (targetKey) =>
                mod === targetKey.toLowerCase()
                || mod.replace(/_/g, '-') === targetKey.replace(/_/g, '-').toLowerCase()
                || mod.includes('timeline'),
            )
          ) {
            if (Array.isArray(item.actions)) actions.push(...item.actions);
            if (Array.isArray(item.tasks)) actions.push(...item.tasks);
          }
        }
      });
    } else if (typeof rawPerms === 'object') {
      Object.entries(rawPerms).forEach(([permKey, permVal]) => {
        const lowerKey = permKey.toLowerCase();
        const isMatch = targetModuleKeys.some(
          (targetKey) =>
            lowerKey === targetKey.toLowerCase()
            || lowerKey.replace(/_/g, '-') === targetKey.replace(/_/g, '-').toLowerCase()
            || lowerKey.includes('timeline'),
        );
        if (isMatch) {
          if (Array.isArray(permVal)) {
            actions.push(...permVal);
          } else if (typeof permVal === 'string') {
            actions.push(permVal);
          }
        }
      });
    }

    return actions.map((actionItem) => String(actionItem).toLowerCase());
  }, [userProfile]);

  const canCreateTimelineRequest = useMemo(
    () =>
      isSuperAdmin
      || timelinePermissions.includes('create')
      || timelinePermissions.includes('add')
      || timelinePermissions.includes('write'),
    [isSuperAdmin, timelinePermissions],
  );
  const canEditTimelineRequest = useMemo(
    () =>
      isSuperAdmin
      || timelinePermissions.includes('update')
      || timelinePermissions.includes('edit')
      || timelinePermissions.includes('modify'),
    [isSuperAdmin, timelinePermissions],
  );
  const canApproveTimelineRequest = useMemo(
    () =>
      isSuperAdmin
      || timelinePermissions.includes('update')
      || timelinePermissions.includes('edit')
      || timelinePermissions.includes('approve'),
    [isSuperAdmin, timelinePermissions],
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search],
  );
  const employeeId = useMemo(
    () =>
      userProfile?.employeeId
      || userProfile?.employee_id
      || userProfile?.empId
      || userProfile?.emp_id
      || userProfile?.id
      || '',
    [userProfile],
  );
  const employeeNo = useMemo(
    () =>
      userProfile?.employeeNo
      || userProfile?.employee_no
      || userProfile?.empNo
      || userProfile?.emp_no
      || '',
    [userProfile],
  );
  const employeeEmail = useMemo(
    () =>
      userProfile?.email
      || userProfile?.emailId
      || userProfile?.email_id
      || userProfile?.loginId
      || userProfile?.login_id
      || '',
    [userProfile],
  );
  const employeeName = useMemo(
    () =>
      userProfile?.name
      || userProfile?.userName
      || userProfile?.user_name
      || '',
    [userProfile],
  );

  const routeState = useMemo(
    () =>
      location.state && typeof location.state === 'object'
        ? location.state
        : {},
    [location.state],
  );

  const scopedProjectId
    = routeState?.projectId
    || searchParams.get('projectId')
    || searchParams.get('id')
    || '';
  const routeProjectName = routeState?.projectName || searchParams.get('projectName') || '';
  const isProjectScopedTimeline = Boolean(scopedProjectId || routeProjectName);

  const [routeScopedFiltersActive, setRouteScopedFiltersActive] = useState(
    () => Boolean(scopedProjectId || routeProjectName),
  );
  const routeScopedFiltersActiveRef = useRef(
    Boolean(scopedProjectId || routeProjectName),
  );

  const updateRouteScopedFiltersActive = useCallback((val) => {
    routeScopedFiltersActiveRef.current = val;
    setRouteScopedFiltersActive(val);
  }, []);

  const [fetchedProjectName, setFetchedProjectName] = useState('');
  const scopedProjectName = routeProjectName || fetchedProjectName || '';

  const initialProjectFilterValue = useMemo(() => {
    if (scopedProjectId) {
      const label = scopedProjectName || routeProjectName || fetchedProjectName || String(scopedProjectId);
      return { value: String(scopedProjectId), label };
    }
    if (routeProjectName || fetchedProjectName) {
      const name = routeProjectName || fetchedProjectName;
      return { value: name, label: name };
    }
    return null;
  }, [scopedProjectId, scopedProjectName, routeProjectName, fetchedProjectName]);

  const initialScopedProjectFilters = useMemo(() => {
    if (!routeScopedFiltersActive || !initialProjectFilterValue) return [];

    return [
      {
        field: 'projectName',
        selectedValue: initialProjectFilterValue,
        selectedValues: [initialProjectFilterValue],
      },
      {
        field: 'requestStatusRaw',
        selectedValue: 'Open',
        selectedValues: ['Open'],
      },
    ];
  }, [routeScopedFiltersActive, initialProjectFilterValue]);

  useEffect(() => {
    let cancelled = false;
    const tryFetchProjectName = async () => {
      if (routeProjectName) return;
      const pid = scopedProjectId || '';
      if (!pid) return;
      if (fetchedProjectName) return;
      try {
        const resp = await UseAxios(
          getProjectDetailsByProjectId(pid, String(Date.now())),
        );
        const payload = resp?.data;
        const data
          = payload && typeof payload === 'object' && payload.data
            ? payload.data
            : payload;
        const name = data?.project_name || data?.projectName || '';
        if (!cancelled && name) setFetchedProjectName(name);
      } catch (err) {
        // ignore
      }
    };
    void tryFetchProjectName();
    return () => {
      cancelled = true;
    };
  }, [routeProjectName, scopedProjectId, fetchedProjectName]);

  const [activeProjectId, setActiveProjectId] = useState(() => scopedProjectId);
  const activeProjectIdRef = useRef(scopedProjectId);
  const [projectMasterOptions, setProjectMasterOptions] = useState([]);
  const projectMasterOptionsRef = useRef([]);
  const isFirstRequest = useRef(true);
  const isFirstMount = useRef(true);
  const isManualSelection = useRef(false);
  const [tableKey, setTableKey] = useState(0);

  const projectFilterWasClearedRef = useRef(false);

  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);

  useEffect(() => {
    const isFirst = isFirstMount.current;
    isFirstMount.current = false;

    const hasRouteScope = Boolean(scopedProjectId || routeProjectName);
    updateRouteScopedFiltersActive(hasRouteScope);
    projectFilterWasClearedRef.current = false;
    setActiveProjectId(scopedProjectId);
    setFetchedProjectName('');
    lastFiltersJsonRef.current = null;

    if (isManualSelection.current) {
      isManualSelection.current = false;
    } else {
      isFirstRequest.current = true;
    }

    if (!isFirst && scopedProjectId === '') {
      setTableKey((prev) => prev + 1);
    }
  }, [scopedProjectId, routeProjectName, updateRouteScopedFiltersActive]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const target = event.target;
      if (!target) return;

      // Case 1: Reset inside a child filter panel (e.g. column menu or sections)
      if (target.classList.contains('tu-outside-filter-reset')) {
        const parent = target.parentElement;
        if (parent) {
          const applyBtn = parent.querySelector('.tu-outside-filter-apply');
          if (applyBtn) {
            setTimeout(() => {
              applyBtn.click();

              // Also find and click the main filter panel's Apply button if it is open
              const mainApplyBtn = document.querySelector(
                '.tu-filter-footer-apply',
              );
              if (mainApplyBtn) {
                setTimeout(() => {
                  mainApplyBtn.click();
                }, 50);
              }
            }, 50);
          }
        }
      }

      // Case 2: Reset inside the main filter panel footer
      if (target.classList.contains('tu-filter-footer-link')) {
        const parent = target.parentElement;
        if (parent) {
          const applyBtn = parent.querySelector('.tu-filter-footer-apply');
          if (applyBtn) {
            setTimeout(() => {
              applyBtn.click();
            }, 50);
          }
        }
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const loadProjectMasterOptions = useCallback(async () => {
    if (projectMasterOptionsRef.current.length > 0) {
      return projectMasterOptionsRef.current;
    }
    try {
      const response = await UseAxios(masterProjectNameData());
      const options = normalizeProjectMasterOptions(
        getProjectNameMasterRows(response),
      );
      if (options.length > 0) {
        projectMasterOptionsRef.current = options;
        setProjectMasterOptions(options);
      }
    } catch {
      // non-critical
    }
    return projectMasterOptionsRef.current;
  }, []);

  useEffect(() => {
    loadProjectMasterOptions();
  }, [loadProjectMasterOptions]);

  const [rows, setRows] = useState([]);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [tableScrollHeight, setTableScrollHeight] = useState('420px');
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const currentPageRef = useRef(1);
  const handlePaginationChange = useCallback((newPagination) => {
    currentPageRef.current = Number(newPagination.page) || 1;
    setPagination(newPagination);
  }, []);
  const tableStateRef = useRef({});
  const lastFiltersJsonRef = useRef(null);
  const configurationRef = useRef(null);
  const [selectedFilterProjectName, setSelectedFilterProjectName] = useState('');
  const selectedFilterProjectNameRef = useRef('');
  const resolvedProjectNameRef = useRef('');
  const scopedRowsRef = useRef([]);
  const [requestDialog, setRequestDialog] = useState({
    open: false,
    mode: 'new',
    request: createEmptyRequest(scopedProjectName),
  });
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    isBulk: true,
    request: { requestStatus: '', approverComment: '' },
  });
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);

  const isProfileReady = Boolean(
    userProfile && typeof userProfile === 'object',
  );

  const scopedRows = useMemo(() => rows, [rows]);

  const resolvedProjectName = useMemo(() => {
    if (selectedFilterProjectName) {
      const pInfo = resolveProjectInfo(
        selectedFilterProjectName,
        projectMasterOptions,
        rows,
      );
      if (pInfo.projectName) return pInfo.projectName;
      return selectedFilterProjectName;
    }
    if (
      scopedProjectName
      && routeScopedFiltersActive
      && (activeProjectId === scopedProjectId || !activeProjectId)
    ) {
      return scopedProjectName;
    }
    if (activeProjectId) {
      const pInfo = resolveProjectInfo(
        activeProjectId,
        projectMasterOptions,
        rows,
      );
      if (pInfo.projectName) return pInfo.projectName;
    }
    return '';
  }, [
    selectedFilterProjectName,
    activeProjectId,
    scopedProjectId,
    scopedProjectName,
    routeScopedFiltersActive,
    projectMasterOptions,
    rows,
  ]);

  const isSingleProjectSelected = useMemo(() => {
    return Boolean(
      selectedFilterProjectName
      || (activeProjectId && (resolvedProjectName || (routeScopedFiltersActive && scopedProjectName)))
      || (scopedProjectId && routeScopedFiltersActive),
    );
  }, [
    selectedFilterProjectName,
    activeProjectId,
    resolvedProjectName,
    scopedProjectId,
    scopedProjectName,
    routeScopedFiltersActive,
  ]);

  useEffect(() => {
    resolvedProjectNameRef.current = resolvedProjectName;
  }, [resolvedProjectName]);

  useEffect(() => {
    scopedRowsRef.current = scopedRows;
  }, [scopedRows]);

  useEffect(() => {
    const handleResize = () => {
      const nextHeight = Math.min(Math.max(window.innerHeight - 280, 360), 520);
      setTableScrollHeight(`${nextHeight}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setRequestDialog((current) =>
      current.open
        ? current
        : {
          ...current,
          request: createEmptyRequest(
            resolvedProjectName
            || (isProjectScopedTimeline ? scopedProjectName : '')
            || '',
          ),
        },
    );
  }, [resolvedProjectName, scopedProjectName, isProjectScopedTimeline]);

  const handleToggleAllRows = useCallback(
    (checked) => {
      const openRows = scopedRows.filter((row) => {
        const status = String(row?.requestStatus || '')
          .trim()
          .toLowerCase();
        return status === 'open' || status === '';
      });
      const availableRowIds = openRows.map((row) => row.id);

      setSelectedRowIds((current) => {
        if (!checked) {
          return current.filter((rowId) => !availableRowIds.includes(rowId));
        }

        return Array.from(new Set([...current, ...availableRowIds]));
      });
    },
    [scopedRows],
  );

  const canUserEditRowRequest = useCallback(
    (row) => {
      if (!row) return false;
      const status = String(row?.requestStatus || '').toLowerCase();
      if (status === 'approved' || status === 'rejected') {
        return false;
      }
      if (isSuperAdmin) return true;
      if (canEditTimelineRequest) return true;
      return false;
    },
    [isSuperAdmin, canEditTimelineRequest],
  );

  const handleRequestFieldChange = useCallback((field, value) => {
    setRequestDialog((current) => ({
      ...current,
      request: {
        ...current.request,
        [field]: value,
        ...(field === 'requestedFieldValue'
          && (value === 'Actual Start Date' || value === 'Replanned End Date')
          ? { approvalLink: '' }
          : {}),
      },
    }));
  }, []);

  const handleStatusFieldChange = useCallback((field, value) => {
    setStatusDialog((current) => ({
      ...current,
      request: {
        ...current.request,
        [field]: value,
      },
    }));
  }, []);

  const closeRequestDialog = useCallback(() => {
    setRequestDialog({
      open: false,
      mode: 'new',
      request: createEmptyRequest(resolvedProjectName),
    });
  }, [resolvedProjectName]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog({
      open: false,
      isBulk: true,
      request: { requestStatus: '', approverComment: '' },
    });
  }, []);

  const getEffectiveProjectName = useCallback(() => {
    if (selectedFilterProjectNameRef.current) {
      const pInfo = resolveProjectInfo(
        selectedFilterProjectNameRef.current,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }
    if (selectedFilterProjectName) {
      const pInfo = resolveProjectInfo(
        selectedFilterProjectName,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }
    if (resolvedProjectNameRef.current) {
      const pInfo = resolveProjectInfo(
        resolvedProjectNameRef.current,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }
    if (resolvedProjectName) {
      const pInfo = resolveProjectInfo(
        resolvedProjectName,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }

    const urlParamProjectName = searchParams.get('projectName');
    if (urlParamProjectName) {
      const pInfo = resolveProjectInfo(
        urlParamProjectName,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
      return urlParamProjectName;
    }

    const urlParamProjectId = searchParams.get('projectId') || searchParams.get('id');
    if (urlParamProjectId) {
      const pInfo = resolveProjectInfo(
        urlParamProjectId,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }

    if (activeProjectId) {
      const pInfo = resolveProjectInfo(
        activeProjectId,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectName) return pInfo.projectName;
    }

    const tableState = typeof tableStateRef.current === 'function' ? tableStateRef.current() : tableStateRef.current;
    const activeFilters = tableState?.filters || tableState?.filter || [];
    const projectFilter = Array.isArray(activeFilters)
      ? activeFilters.find((filterItem) => filterItem?.field === 'projectName')
      : null;

    if (projectFilter) {
      const rawVal = projectFilter.selectedValue ?? projectFilter.selectedValues;
      const rawArr = Array.isArray(rawVal) ? rawVal : (rawVal !== undefined && rawVal !== null ? [rawVal] : []);
      if (rawArr.length === 1) {
        const pInfo = resolveProjectInfo(
          rawArr[0],
          projectMasterOptionsRef.current || projectMasterOptions,
          scopedRowsRef.current || rows || [],
        );
        if (pInfo.projectName) return pInfo.projectName;
      }
    }

    if (scopedProjectName) return scopedProjectName;

    const rowProjectNames = Array.from(
      new Set(
        (scopedRowsRef.current || rows || [])
          .map((rowItem) => String(rowItem?.projectName || '').trim())
          .filter(Boolean),
      ),
    );
    if (rowProjectNames.length === 1) {
      return rowProjectNames[0];
    }

    return '';
  }, [
    selectedFilterProjectName,
    resolvedProjectName,
    searchParams,
    activeProjectId,
    scopedProjectName,
    projectMasterOptions,
    rows,
  ]);

  const getEffectiveProjectId = useCallback((targetProjectName) => {
    if (targetProjectName) {
      const pInfo = resolveProjectInfo(
        targetProjectName,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectId) return pInfo.projectId;
    }

    if (activeProjectIdRef.current) return activeProjectIdRef.current;
    if (activeProjectId) return activeProjectId;
    if (scopedProjectId) return scopedProjectId;

    const urlParamProjectId = searchParams.get('projectId') || searchParams.get('id');
    if (urlParamProjectId) return urlParamProjectId;

    if (selectedFilterProjectNameRef.current) {
      const pInfo = resolveProjectInfo(
        selectedFilterProjectNameRef.current,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      if (pInfo.projectId) return pInfo.projectId;
    }

    const currentScopedRows = scopedRowsRef.current || rows || [];
    if (currentScopedRows.length > 0) {
      const rowIds = Array.from(
        new Set(
          currentScopedRows
            .map((rowItem) => String(rowItem?.projectId || rowItem?.project_id || '').trim())
            .filter(Boolean),
        ),
      );
      if (rowIds.length === 1) return rowIds[0];
    }

    return '';
  }, [activeProjectId, scopedProjectId, searchParams, projectMasterOptions, rows]);

  const handleCreateRequest = useCallback(() => {
    if (!canCreateTimelineRequest) {
      snackbarOpen(
        'You do not have permission to create timeline change requests.',
        'error',
      );
      return;
    }

    const currentProjectName = getEffectiveProjectName();

    if (!currentProjectName) {
      snackbarOpen(
        'Please select a project in the filter to create a timeline change request.',
        'info',
      );
      return;
    }

    const currentProjectId = getEffectiveProjectId(currentProjectName);

    const currentScopedRows = scopedRowsRef.current || rows || [];

    const hasOpenRequestForProject = currentScopedRows.some(
      (row) =>
        (matchesTimelineScope(row, currentProjectId, currentProjectName)
          || String(row?.projectName || '')
            .trim()
            .toLowerCase() === currentProjectName.trim().toLowerCase())
        && isOpenTimelineRequest(row),
    );

    if (hasOpenRequestForProject) {
      snackbarOpen(
        'An open timeline change request already exists for this project.',
        'error',
      );
      return;
    }

    setRequestDialog({
      open: true,
      mode: 'new',
      request: createEmptyRequest(currentProjectName),
    });
  }, [
    canCreateTimelineRequest,
    getEffectiveProjectName,
    getEffectiveProjectId,
    rows,
    snackbarOpen,
  ]);

  const handleSubmitRequest = useCallback(async () => {
    if (isRequestSubmitting) return;

    const mode = requestDialog.mode;
    if (mode === 'new' && !canCreateTimelineRequest) {
      snackbarOpen(
        'You do not have permission to create timeline change requests.',
        'error',
      );
      return;
    }
    if (mode === 'edit' && !canUserEditRowRequest(requestDialog.request)) {
      snackbarOpen(
        'You do not have permission to edit this timeline request.',
        'error',
      );
      return;
    }

    const form = requestDialog.request;
    const rawProjectInput = String(
      form.projectName || getEffectiveProjectName() || '',
    ).trim();

    const pInfo = resolveProjectInfo(
      rawProjectInput,
      projectMasterOptionsRef.current || projectMasterOptions,
      scopedRowsRef.current || rows || [],
    );

    const projectName = pInfo.projectName || rawProjectInput;
    const resolvedProjectId
      = form.projectId
      || pInfo.projectId
      || getEffectiveProjectId(projectName)
      || activeProjectId
      || scopedProjectId;

    if (!projectName || !form.requestedFieldValue || !form.newDateValue) {
      snackbarOpen(
        'Project name, date type, and new date are required.',
        'error',
      );
      return;
    }

    const normalizedProjectName = projectName.toLowerCase();
    const existingRow = rows.find(
      (row) =>
        String(row?.projectName || '')
          .trim()
          .toLowerCase() === normalizedProjectName
        || String(row?.projectId || row?.project_id || '') === String(resolvedProjectId || ''),
    );

    const openRequestForProject = rows.find(
      (row) =>
        isOpenTimelineRequest(row)
        && (String(row?.projectName || '')
          .trim()
          .toLowerCase() === normalizedProjectName
          || String(row?.projectId || '') === String(resolvedProjectId || '')),
    );

    if (!resolvedProjectId) {
      snackbarOpen(
        'Project id is required. Open Timeline from project details or use an existing project name.',
        'error',
      );
      return;
    }

    if (requestDialog.mode === 'new' && openRequestForProject) {
      snackbarOpen(
        'An open timeline change request already exists for this project.',
        'error',
      );
      return;
    }

    const payload = {
      projectId: Number(resolvedProjectId),
      projectName,
      projectVertical:
        form.projectVertical || pInfo.projectVertical || existingRow?.projectVertical || '',
      requestedFieldValue: form.requestedFieldValue,
      newDate: form.newDateValue,
      approvalLink: form.approvalLink || '',
      reasonForChange: form.reasonForChange || '',
      logged_in_emp_id: employeeId,
      employeeId,
      id: userProfile?.id || '',
      employeeNo,
      empNo: employeeNo,
      emailId: employeeEmail,
      name: employeeName,
      isSuperAdmin,
      super_admin: isSuperAdmin,
      payload: {
        userInfo: {
          employeeId,
          id: userProfile?.id || '',
          employeeNo,
          emailId: employeeEmail,
          name: employeeName,
          isSuperAdmin,
          super_admin: isSuperAdmin,
          permissions: userProfile?.permissions,
        },
      },
    };

    try {
      setIsRequestSubmitting(true);
      const response
        = requestDialog.mode === 'edit'
          ? await UseAxios(updateTimelineRequest(form.id, payload))
          : await UseAxios(createTimelineRequest(payload));

      const responsePayload = response?.data;
      if (!responsePayload || isAxiosFailurePayload(responsePayload)) {
        throw (
          responsePayload || new Error('Failed to submit timeline request.')
        );
      }
      if (
        typeof responsePayload === 'object'
        && responsePayload !== null
        && responsePayload.success === false
      ) {
        throw new Error(
          responsePayload.message || 'Failed to submit timeline request.',
        );
      }

      closeRequestDialog();
      snackbarOpen(
        requestDialog.mode === 'edit'
          ? 'Timeline request updated.'
          : 'Timeline request created.',
        'success',
      );
    } catch (error) {
      snackbarOpen(
        getApiErrorMessage(error, 'Failed to submit timeline request.'),
        'error',
      );
    } finally {
      setIsRequestSubmitting(false);
    }
  }, [
    closeRequestDialog,
    employeeEmail,
    employeeId,
    employeeName,
    employeeNo,
    isRequestSubmitting,
    isSuperAdmin,
    requestDialog,
    rows,
    activeProjectId,
    snackbarOpen,
    userProfile,
  ]);

  const handleOpenBulkStatusDialog = useCallback(() => {
    if (!canApproveTimelineRequest) {
      snackbarOpen(
        'You do not have permission to update timeline request status.',
        'error',
      );
      return;
    }

    if (!selectedRowIds.length) {
      snackbarOpen(
        'Select at least one timeline request to update status.',
        'error',
      );
      return;
    }

    const selectedRows = rows.filter((row) => selectedRowIds.includes(row.id));
    const openRequests = selectedRows.filter((row) => {
      const status = String(row?.requestStatus || '')
        .trim()
        .toLowerCase();
      return status === 'open' || status === '';
    });

    if (openRequests.length === 0) {
      snackbarOpen(
        'All selected requests have already been processed. Only open requests can be updated.',
        'error',
      );
      return;
    }

    if (openRequests.length < selectedRowIds.length) {
      const skippedCount = selectedRowIds.length - openRequests.length;
      snackbarOpen(
        `${skippedCount} already-processed request(s) will be skipped. Only open requests can be updated.`,
        'warning',
      );
    }

    setStatusDialog({
      open: true,
      isBulk: openRequests.length > 1,
      request: {
        projectName: resolvedProjectName,
        requestStatus: '',
        approverComment: '',
        srnNo: openRequests.length === 1 ? openRequests[0]?.srnNo || '' : '',
      },
    });
  }, [resolvedProjectName, rows, selectedRowIds, snackbarOpen]);

  const toolbarAdditionalElements = useMemo(
    () => [<TimelineToolbarActions key="timeline-toolbar-actions" />],
    [],
  );

  const handleSubmitStatus = useCallback(async () => {
    if (isStatusSubmitting) return;

    if (!canApproveTimelineRequest) {
      snackbarOpen(
        'You do not have permission to update timeline request status.',
        'error',
      );
      return;
    }

    const { requestStatus, approverComment } = statusDialog.request;
    if (!requestStatus || !approverComment.trim()) {
      snackbarOpen('Request status and comment are required.', 'error');
      return;
    }

    const selectedRows = rows.filter((row) => selectedRowIds.includes(row.id));
    const openRequests = selectedRows.filter((row) => {
      const status = String(row?.requestStatus || '')
        .trim()
        .toLowerCase();
      return status === 'open' || status === '';
    });
    const validRequestIds = openRequests.map((row) => row.id);

    if (validRequestIds.length === 0) {
      snackbarOpen(
        'All selected requests have already been processed. Only open requests can be updated.',
        'error',
      );
      return;
    }

    try {
      setIsStatusSubmitting(true);
      const response = await UseAxios(
        updateTimelineRequestStatus({
          requestIds: validRequestIds,
          requestStatus,
          approverComment,
          logged_in_emp_id: employeeId,
          employeeId,
          id: userProfile?.id || '',
          employeeNo,
          empNo: employeeNo,
          emailId: employeeEmail,
          name: employeeName,
          isSuperAdmin,
          super_admin: isSuperAdmin,
          payload: {
            userInfo: {
              employeeId,
              id: userProfile?.id || '',
              employeeNo,
              emailId: employeeEmail,
              name: employeeName,
              isSuperAdmin,
              super_admin: isSuperAdmin,
              permissions: userProfile?.permissions,
            },
          },
        }),
      );

      const payload = response?.data;
      if (!payload || isAxiosFailurePayload(payload)) {
        throw payload || new Error('Failed to update timeline request status.');
      }
      if (
        typeof payload === 'object'
        && payload !== null
        && payload.success === false
      ) {
        throw new Error(
          payload.message || 'Failed to update timeline request status.',
        );
      }

      setSelectedRowIds([]);
      closeStatusDialog();
      const successMessage
        = payload.message || 'Timeline request status updated.';
      snackbarOpen(successMessage, 'success');
    } catch (error) {
      snackbarOpen(
        getApiErrorMessage(error, 'Failed to update timeline request status.'),
        'error',
      );
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [
    closeStatusDialog,
    isStatusSubmitting,
    isSuperAdmin,
    rows,
    selectedRowIds,
    snackbarOpen,
    statusDialog.request,
    employeeId,
    userProfile,
    employeeNo,
    employeeEmail,
    employeeName,
  ]);

  const buildSrnCell = useCallback(
    (row) => (
      <SrnRowCell
        rowId={row.id}
        srnNo={row.srnNo}
        requestStatus={row.requestStatus}
      />
    ),
    [],
  );

  const buildActionCell = useCallback(
    (row) => {
      const status = String(row?.requestStatus || '').toLowerCase();
      const isResolved = status === 'approved' || status === 'rejected';
      const canModifyRequest = canUserEditRowRequest(row);

      if (!canModifyRequest) return null;

      return (
        <button
          type="button"
          className="timeline-action-btn"
          disabled={isResolved}
          title={
            isResolved
              ? `Cannot edit ${status} request`
              : 'Edit timeline request'
          }
          onClick={() => {
            if (isResolved) {
              snackbarOpen(
                `The request cannot be edited as it is already ${status}.`,
                'error',
              );
              return;
            }
            if (!canModifyRequest) {
              snackbarOpen(
                'You do not have permission to edit timeline requests.',
                'error',
              );
              return;
            }
            setRequestDialog({
              open: true,
              mode: 'edit',
              request: {
                id: row.id,
                projectId: row.projectId,
                projectName: row.projectName,
                projectVertical: row.projectVertical,
                requestedFieldValue:
                  row.requestedField || row.requestedFieldValue,
                newDateValue: row.newDate || row.newDateValue,
                approvalLink: row.approvalLink,
                reasonForChange: row.reasonForChange,
              },
            });
          }}
        >
          <img src={EditIcon} alt="Edit" className="timeline-edit-icon" />
        </button>
      );
    },
    [canUserEditRowRequest, snackbarOpen, setRequestDialog],
  );

  const buildApprovalLinkCell = useCallback((row) => {
    const link = row?.approvalLink || '';
    if (!link || link === '-' || link.trim() === '') {
      return '-';
    }
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="timeline-link"
        onClick={(event) => event.stopPropagation()}
      >
        {link}
      </a>
    );
  }, []);

  const buildStatusCell = useCallback((row) => {
    const status = String(row?.requestStatus || '').trim();
    const statusClass = __STATUS_CLASS_MAP[status] || 'status-open';

    return (
      <span className={`timeline-status-chip ${statusClass}`}>
        {status || 'Open'}
      </span>
    );
  }, []);

  /* --- Fixed Request Mapping: Inject custom dropdown metrics inside 'payload' --- */
  const handleFilteredApiRequest = (requestData) => {
    const isFirst = isFirstRequest.current;
    isFirstRequest.current = false;
    const requestFilters = {};
    const scopedProjectValue = routeProjectName || scopedProjectId;
    if (Array.isArray(requestData.filter)) {
      requestData.filter.forEach((item) => {
        if (item && item.field) {
          const selectedValue
            = normalizeFilterSelectionValue(item.selectedValue)
            || cloneSelectedValues(item.selectedValues);
          requestFilters[item.field] = selectedValue;
          if (
            item.field === 'requestStatus'
            || item.field === 'requestStatusRaw'
          ) {
            requestFilters.requestStatus = selectedValue;
            requestFilters.requestStatusRaw = selectedValue;
          }
        }
      });
    }

    const activeFilters = Array.isArray(requestData.filter)
      ? requestData.filter
      : [];
    const hasProjectNameFilter = activeFilters.some(
      (filterItem) => filterItem?.field === 'projectName',
    );
    if (!hasProjectNameFilter && routeScopedFiltersActiveRef.current && scopedProjectValue) {
      const initialVal = initialProjectFilterValue || scopedProjectValue;
      activeFilters.push({
        field: 'projectName',
        selectedValue: initialVal,
        selectedValues: [initialVal],
      });
      requestFilters.projectName = initialVal;
    }
    const hasRequestStatusFilter = activeFilters.some(
      (filterItem) =>
        (filterItem?.field === 'requestStatus'
          || filterItem?.field === 'requestStatusRaw')
        && (filterItem.selectedValue != null
          || (Array.isArray(filterItem.selectedValues)
            && filterItem.selectedValues.length > 0)),
    );
    if (routeScopedFiltersActiveRef.current && isProjectScopedTimeline && !hasRequestStatusFilter) {
      activeFilters.push({
        field: 'requestStatusRaw',
        selectedValue: 'Open',
        selectedValues: ['Open'],
      });
      requestFilters.requestStatus = 'Open';
      requestFilters.requestStatusRaw = 'Open';
    }

    // ── Project-name filter handling ──────────────────────────────────────────
    let projectFilterItem = activeFilters.find(
      (fi) => fi.field === 'projectName',
    );

    if (!projectFilterItem && routeScopedFiltersActiveRef.current && isProjectScopedTimeline) {
      const pInfo = resolveProjectInfo(
        scopedProjectName || scopedProjectId,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      );
      const initialVal = initialProjectFilterValue || pInfo.projectName || scopedProjectName || scopedProjectId;
      projectFilterItem = {
        field: 'projectName',
        selectedValue: initialVal,
        selectedValues: [initialVal],
      };
      activeFilters.push(projectFilterItem);
      requestFilters.projectName = initialVal;
    }

    const hasProjectNameSelected
      = projectFilterItem
      && ((projectFilterItem.selectedValue != null
        && projectFilterItem.selectedValue !== '')
        || (Array.isArray(projectFilterItem.selectedValues)
          && projectFilterItem.selectedValues.length > 0));

    let selectedProjectRawValues = [];
    if (projectFilterItem) {
      if (
        Array.isArray(projectFilterItem.selectedValues)
        && projectFilterItem.selectedValues.length > 0
      ) {
        selectedProjectRawValues = projectFilterItem.selectedValues;
      } else if (projectFilterItem.selectedValue) {
        const val = projectFilterItem.selectedValue;
        selectedProjectRawValues = Array.isArray(val) ? val : [val];
      }
    }

    const resolvedProjectList = selectedProjectRawValues.map((val) =>
      resolveProjectInfo(
        val,
        projectMasterOptionsRef.current || projectMasterOptions,
        scopedRowsRef.current || rows || [],
      ),
    );

    const selectedProjectNames = resolvedProjectList
      .map((projectItem) => projectItem.projectName)
      .filter(Boolean);
    const selectedProjectIds = resolvedProjectList
      .map((projectItem) => projectItem.projectId)
      .filter(Boolean);

    const effectiveFilterProjectName
      = selectedProjectNames.length === 1 ? selectedProjectNames[0] : '';
    selectedFilterProjectNameRef.current = effectiveFilterProjectName;
    setSelectedFilterProjectName(effectiveFilterProjectName);

    // ── Resolve projectId from the project-name filter ────────────────────────
    let nextProjectId = activeProjectIdRef.current || scopedProjectId;
    if (hasProjectNameSelected) {
      if (selectedProjectIds.length === 1) {
        nextProjectId = selectedProjectIds[0];
      } else if (selectedProjectNames.length === 1) {
        const pInfo = resolveProjectInfo(
          selectedProjectNames[0],
          projectMasterOptionsRef.current || projectMasterOptions,
          scopedRowsRef.current || rows || [],
        );
        nextProjectId = pInfo.projectId || nextProjectId;
      }
    }
    if (
      !isFirst
      && !hasProjectNameSelected
      && activeProjectIdRef.current !== ''
    ) {
      // Filter was cleared (Reset pressed or filter item removed).
      nextProjectId = '';
    }

    // ── Page-reset on filter change ───────────────────────────────────────────
    const currentFiltersJson = JSON.stringify(activeFilters);
    const filtersChanged
      = lastFiltersJsonRef.current !== null
      && lastFiltersJsonRef.current !== currentFiltersJson;
    lastFiltersJsonRef.current = currentFiltersJson;

    let page = Number(requestData?.pagination?.page) || 1;
    const limit = Number(requestData?.pagination?.limit) || 10;
    if (filtersChanged) {
      page = 1;
    }
    currentPageRef.current = page;

    // Mutate requestData in place so TableUtility registers the correct page
    requestData.page = page;
    requestData.limit = limit;
    if (requestData.pagination) {
      requestData.pagination.page = page;
      requestData.pagination.limit = limit;
    }

    const formattedRequest = {
      ...requestData,
      filter: activeFilters,
      page,
      limit,
      pagination: {
        page,
        limit,
      },
      selectedViewId: requestData.selectedViewId || null,
      payload: {
        userInfo: {
          id: userProfile?.id,
          emailId: userProfile?.email,
          employeeId: userProfile?.employeeId,
          empId: userProfile?.empId,
          employeeNo: userProfile?.employeeNo,
          name: userProfile?.name,
          permissions: userProfile?.permissions,
          roleDetails: userProfile?.roleDetails,
          isSuperAdmin: userProfile?.isSuperAdmin,
        },
        additionalPayloadData: {
          ...requestFilters,
          ...(nextProjectId
          || (routeScopedFiltersActiveRef.current ? scopedProjectId : '')
            ? {
              projectId:
                  nextProjectId
                  || (routeScopedFiltersActiveRef.current ? scopedProjectId : ''),
            }
            : {}),
          ...(effectiveFilterProjectName
          || (routeScopedFiltersActiveRef.current ? scopedProjectName : '')
            ? {
              projectName:
                  effectiveFilterProjectName
                  || (routeScopedFiltersActiveRef.current ? scopedProjectName : ''),
            }
            : {}),
        },
      },
    };

    return formattedRequest;
  };

  const handleFilterUpdate = useCallback(
    ({ action, filters }) => {
      if (action === 'reset') {
        activeProjectIdRef.current = '';
        selectedFilterProjectNameRef.current = '';
        resolvedProjectNameRef.current = '';
        tableStateRef.current = {};
        lastFiltersJsonRef.current = null;
        isFirstRequest.current = false;
        projectFilterWasClearedRef.current = true;
        setActiveProjectId('');
        setSelectedFilterProjectName('');
        updateRouteScopedFiltersActive(false);
      } else if (action === 'apply') {
        const filterArray = Array.isArray(filters) ? filters : [];
        const projectFilterItem = filterArray.find(
          (item) => item?.field === 'projectName',
        );

        let selectedProjectRawValues = [];
        if (projectFilterItem) {
          if (
            Array.isArray(projectFilterItem.selectedValues)
            && projectFilterItem.selectedValues.length > 0
          ) {
            selectedProjectRawValues = projectFilterItem.selectedValues;
          } else if (projectFilterItem.selectedValue) {
            const val = projectFilterItem.selectedValue;
            selectedProjectRawValues = Array.isArray(val) ? val : [val];
          }
        }

        const selectedProjectObjs = selectedProjectRawValues
          .map((item) => {
            if (item && typeof item === 'object') {
              return {
                id: item.value ?? item.id ?? item.projectId,
                label: item.label ?? item.name ?? item.projectName,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (selectedProjectObjs.length > 0) {
          const firstObj = selectedProjectObjs[0];
          const newId = String(firstObj.id || '').trim();
          const newLabel = String(firstObj.label || '').trim();

          activeProjectIdRef.current = newId;
          selectedFilterProjectNameRef.current = newLabel;
          projectFilterWasClearedRef.current = false;
          setActiveProjectId(newId);
          setSelectedFilterProjectName(newLabel);
          updateRouteScopedFiltersActive(false);
        } else if (selectedProjectRawValues.length > 0) {
          const rawVal = selectedProjectRawValues[0];
          const strVal = String(rawVal).trim();
          const pInfo = resolveProjectInfo(
            strVal,
            projectMasterOptionsRef.current || projectMasterOptions,
            scopedRowsRef.current || rows || [],
          );
          const newId = pInfo.projectId || strVal;
          const newLabel = pInfo.projectName || strVal;

          activeProjectIdRef.current = newId;
          selectedFilterProjectNameRef.current = newLabel;
          projectFilterWasClearedRef.current = false;
          setActiveProjectId(newId);
          setSelectedFilterProjectName(newLabel);
          updateRouteScopedFiltersActive(false);
        } else {
          activeProjectIdRef.current = '';
          selectedFilterProjectNameRef.current = '';
          resolvedProjectNameRef.current = '';
          projectFilterWasClearedRef.current = true;
          setActiveProjectId('');
          setSelectedFilterProjectName('');
          updateRouteScopedFiltersActive(false);
        }
      }
    },
    [updateRouteScopedFiltersActive, projectMasterOptions, rows],
  );

  /* --- Fixed Response Mapping: Synchronized key extraction to perfectly match BE fields --- */
  const handleFilteredApiResponse = useCallback(
    (response) => {
      const normalized = normalizeTableUtilityResponse(response);
      syncPaginationFromResponse(setPagination, normalized);

      const rawData
        = normalized?.data && typeof normalized.data === 'object'
          ? normalized.data
          : {};
      const meta = rawData.meta || normalized?.data?.meta || {};
      if (meta.page) {
        currentPageRef.current = Number(meta.page) || 1;
      }

      const totalCount = Number(meta.total) || 0;
      const limitCount = Number(meta.limit) || 10;
      if (configurationRef.current) {
        configurationRef.current.tableSettings.paginationConfig.total
          = totalCount;
        configurationRef.current.tableSettings.paginationConfig.limit
          = limitCount;
        configurationRef.current.tableSettings.pageSize = limitCount;
        if (configurationRef.current.tableSettings.loaderSkeleton) {
          configurationRef.current.tableSettings.loaderSkeleton.rows
            = limitCount;
        }
      }

      // Raw response shape: { data: [...rows], filterOptions: {...}, pagination: {...} }
      const apiRows = Array.isArray(rawData.tableData)
        ? rawData.tableData
        : Array.isArray(rawData.rows)
          ? rawData.rows
          : Array.isArray(rawData.data)
            ? rawData.data
            : [];
      setRows(apiRows);

      // filterOptions sits at rawData.filterOptions (same level as rawData.data in raw response)
      const responseFilterOptions = normalizeTimelineFilterOptions(
        rawData.filterOptions
        || normalized?.filterOptions
        || rawData.data?.filterOptions
        || null,
      );

      // Inject fresh tableConfiguration with API filter options directly into response.data.
      // TableUtility reads tableConfiguration from the response object itself, so this ensures
      // the filter dropdowns are populated on every API call (React state updates are too late).
      if (Object.keys(responseFilterOptions).length > 0) {
        const currentActiveId = activeProjectIdRef.current;
        const currentActiveName = selectedFilterProjectNameRef.current;

        let selectedProjectValue = null;
        if (currentActiveId && currentActiveName) {
          selectedProjectValue = {
            value: String(currentActiveId),
            label: String(currentActiveName),
          };
        } else if (currentActiveId) {
          const pInfo = resolveProjectInfo(
            currentActiveId,
            projectMasterOptionsRef.current || projectMasterOptions,
            apiRows,
          );
          selectedProjectValue = {
            value: String(currentActiveId),
            label: pInfo.projectName || String(currentActiveId),
          };
        } else if (currentActiveName) {
          const pInfo = resolveProjectInfo(
            currentActiveName,
            projectMasterOptionsRef.current || projectMasterOptions,
            apiRows,
          );
          selectedProjectValue = {
            value: pInfo.projectId || currentActiveName,
            label: currentActiveName,
          };
        } else if (
          routeScopedFiltersActiveRef.current
          && !projectFilterWasClearedRef.current
          && (scopedProjectId || scopedProjectName)
        ) {
          selectedProjectValue = initialProjectFilterValue;
        }

        const freshInitialSelected = {
          ...(selectedProjectValue ? { projectName: [selectedProjectValue] } : {}),
          ...(routeScopedFiltersActiveRef.current
          && !projectFilterWasClearedRef.current
          && isProjectScopedTimeline
            ? { requestStatus: ['Open'], requestStatusRaw: ['Open'] }
            : {}),
        };
        const beTableConfig
          = Array.isArray(rawData.tableConfiguration)
            && rawData.tableConfiguration.length > 0
            ? rawData.tableConfiguration
            : Array.isArray(rawData.data?.tableConfiguration)
              && rawData.data.tableConfiguration.length > 0
              ? rawData.data.tableConfiguration
              : buildTimelineTableColumns(
                responseFilterOptions || {},
                freshInitialSelected,
              );

        const canShowActionColumn = isSuperAdmin || canEditTimelineRequest;

        const freshTableConfig = beTableConfig.map((column) => {
          const filterOptions = responseFilterOptions[column.field];
          const columnFilterField = column.filterField || column.field;
          const selectedValues
            = freshInitialSelected[columnFilterField]
            || freshInitialSelected[column.field];
          const columnWithOptions
            = Array.isArray(filterOptions) || selectedValues
              ? {
                ...column,
                menuItems: (column.menuItems || []).map((menuItem) => ({
                  ...menuItem,
                  items: (menuItem.items || []).map((item) =>
                    item.type === 'filter'
                      ? {
                        ...item,
                        ...(Array.isArray(filterOptions)
                          ? { options: filterOptions }
                          : {}),
                        ...(freshInitialSelected[item.field]
                          ? {
                            selectedValues:
                              freshInitialSelected[item.field],
                          }
                          : selectedValues
                            ? { selectedValues }
                            : {}),
                      }
                      : item,
                  ),
                })),
              }
              : column;

          if (column.field === 'srnNo') {
            return {
              ...columnWithOptions,
              isPinned: true,
              defaultPinned: true,
              pinnable: false,
              visible: true,
              showFilterButtonInHeader: false,
              showPinButtonInHeader: false,
              header: <SrnHeaderCell />,
            };
          }
          if (column.field === 'action' && !canShowActionColumn) {
            return {
              ...columnWithOptions,
              visible: false,
            };
          }
          return columnWithOptions;
        });

        normalized.data = {
          ...rawData,
          tableConfiguration: freshTableConfig,
          filterOptions: responseFilterOptions,
        };
      }

      const enrichedRows = apiRows.map((row) => ({
        ...row,
        srnNo: buildSrnCell(row),
        action: buildActionCell(row),
        approvalLink: buildApprovalLinkCell(row),
        requestStatus: buildStatusCell(row),
      }));

      // Preserve tableConfiguration set above, add enriched rows
      normalized.data = {
        ...rawData,
        ...(normalized.data || {}),
        tableData: enrichedRows,
      };

      return normalized;
    },
    [buildSrnCell, buildActionCell, buildApprovalLinkCell, buildStatusCell],
  );

  const configuration = useMemo(() => {
    const filterBaseURL = TIMELINE_TABLE_BASE_URL;

    const config = {
      generalSettings: {
        baseURL: filterBaseURL,
        additionalPayloadData: {
          ...(routeScopedFiltersActive && scopedProjectId ? { projectId: scopedProjectId } : {}),
          ...(routeScopedFiltersActive && routeProjectName ? { projectName: routeProjectName } : {}),
        },
        enableApiFiltering: true,
        userInfo: {
          id: undefined,
          emailId: stableUserProfile?.email,
          permissions: stableUserProfile?.permissions,
          roleDetails: stableUserProfile?.roleDetails,
          employeeId: stableUserProfile?.employeeId,
          globalColors: stableUserProfile?.siteConfiguration?.global_colors,
          isSuperAdmin: stableUserProfile?.isSuperAdmin,
          empId: stableUserProfile?.empId,
          employeeNo: stableUserProfile?.employeeNo,
          name: stableUserProfile?.name,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: 'view', enabled: false },
          deleteView: { endpoint: 'view-delete', enabled: false },
          download: { endpoint: 'export', enabled: false },
        },
        handler: {
          handleFilteredApiRequest,
          handleFilteredApiResponse,
          onFilterUpdate: handleFilterUpdate,
          setTableStateHandler: (stateValue) => {
            const resolvedState
              = typeof stateValue === 'function' ? stateValue() : stateValue;
            if (resolvedState && typeof resolvedState === 'object') {
              tableStateRef.current = resolvedState;
            }
            if (resolvedState?.pagination) {
              const nextPage = Number(resolvedState.pagination.page) || 1;
              const nextLimit = Number(resolvedState.pagination.limit) || 10;
              setPagination((prev) => {
                if (prev.page === nextPage && prev.limit === nextLimit)
                  return prev;
                return { ...prev, page: nextPage, limit: nextLimit };
              });
            }
          },
        },
        headers: {
          token: stableUserProfile?.access_token || '',
        },
        showPagination: true,
        showToolBar: true,
        showTieredMenu: true,
        toolBarSettings: {
          showToolBarHeading: true,
          toolBarHeading: 'Timeline Change Request',
          showAddNew: Boolean(canCreateTimelineRequest),
          showDownloadExcel: false,
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showFilterOption: true,
          showAppliedFilters: true,
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          additionalElements: toolbarAdditionalElements,
          handleAddNew: handleCreateRequest,
        },
        handlers: {
          handleAddIconClick: handleCreateRequest,
          handleFilteredApiResponse,
        },
      },
      showFilterOption: true,
      tableSettings: {
        size: 'small',
        pageSize: pagination.limit || 10,
        loaderSkeleton: {
          rows: pagination.limit || 10,
          bodyCells: 2,
          columns: 8,
        },
        paginationConfig: {
          page: currentPageRef.current,
          limit: pagination.limit || 10,
          total: pagination.total || 0,
          onPaginationChange: handlePaginationChange,
          rowsPerPageOptions: [],
          showRowsPerPage: false,
        },
        showExpandCollapse: false,
        tableScrollHeight: '70vh',
        maxFrozenColumns: {
          desktop: 20,
          tablet: 20,
          mobile: 20,
        },
      },
    };
    configurationRef.current = config;
    return config;
  }, [
    toolbarAdditionalElements,
    stableUserProfile,
    scopedProjectId,
    routeProjectName,
    routeScopedFiltersActive,
    canCreateTimelineRequest,
    isSingleProjectSelected,
    pagination.total,
    handleCreateRequest,
    handleFilteredApiResponse,
    handleFilterUpdate,
    handlePaginationChange,
  ]);

  const selectionContextValue = useMemo(() => {
    const openRows = scopedRows.filter((row) => {
      const status = String(row?.requestStatus || '')
        .trim()
        .toLowerCase();
      return status === 'open' || status === '';
    });
    const isAllVisibleRowsSelected
      = openRows.length > 0
      && openRows.every((row) => selectedRowIds.includes(row.id));
    const isSomeSelected
      = selectedRowIds.length > 0 && !isAllVisibleRowsSelected;

    return {
      selectedRowIds,
      setSelectedRowIds,
      isAllVisibleRowsSelected,
      isSomeSelected,
      handleToggleAllRows,
      handleOpenBulkStatusDialog,
      canApproveTimelineRequest,
    };
  }, [
    scopedRows,
    selectedRowIds,
    handleToggleAllRows,
    handleOpenBulkStatusDialog,
    canApproveTimelineRequest,
  ]);

  if (!isProfileReady) {
    return <Loader text="Loading timeline..." />;
  }

  return (
    <TimelineSelectionContext.Provider value={selectionContextValue}>
      <div className="timeline-page">
        <div
          className="timeline-table-utility"
          style={{ '--timeline-scroll-height': tableScrollHeight }}
        >
          <TableUtility
            key={tableKey}
            configuration={configuration}
            externalFilters={initialScopedProjectFilters}
            showFilterOption
          />
        </div>

        <TimelineRequestDialog
          isOpen={requestDialog.open}
          mode={requestDialog.mode}
          request={requestDialog.request}
          dateTypeOptions={TIMELINE_DATE_TYPE_OPTIONS}
          onChange={handleRequestFieldChange}
          onClose={closeRequestDialog}
          onSubmit={handleSubmitRequest}
          isSubmitting={isRequestSubmitting}
        />

        <TimelineStatusDialog
          isOpen={statusDialog.open}
          isBulk={statusDialog.isBulk}
          request={statusDialog.request}
          selectedCount={selectedRowIds.length}
          projectName={resolvedProjectName}
          statusOptions={STATUS_UPDATE_OPTIONS}
          onChange={handleStatusFieldChange}
          onClose={closeStatusDialog}
          onSubmit={handleSubmitStatus}
          isSubmitting={isStatusSubmitting}
        />
      </div>
    </TimelineSelectionContext.Provider>
  );
};

export default Timeline;
